import Button from 'carbon-react/lib/components/button';
import Card, { CardColumn, CardRow } from 'carbon-react/lib/components/card';
import Dialog from 'carbon-react/lib/components/dialog';
import Form from 'carbon-react/lib/components/form';
import Heading from 'carbon-react/lib/components/heading';
import Loader from 'carbon-react/lib/components/loader';
import Message from 'carbon-react/lib/components/message';
import Textbox from 'carbon-react/lib/components/textbox';
import { useState } from 'react';
import GoBackButton from '../../components/GoBackButton';
import { send } from '../../helpers/requests';

const ChangeAdminPermissions = ({ makeAdmin = false }) => {
  const [requestError, setRequestError] = useState<Error>();
  const [requestSuccess, setRequestSuccess] = useState<boolean>();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => ({
    intacctCompanyId: '',
    username: '',
    email: '',
  }));
  const [visited, setVisited] = useState(() => ({
    intacctCompanyId: false,
    username: false,
    email: false,
  }));

  const errors = Object.keys(formData).filter(
    (key) =>
      formData[key as keyof typeof formData] === '' &&
      visited[key as keyof typeof visited] === true,
  ).length;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setVisited((prevState) => ({
      ...prevState,
      [name]: true,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setRequestError(undefined);
      setRequestSuccess(false);
      setLoading(true);
      if (errors > 0) return;
      if (!formData.intacctCompanyId || !formData.username || !formData.email) {
        setVisited({
          intacctCompanyId: true,
          username: true,
          email: true,
        });
        return;
      }
      const data = {
        ...formData,
        role: makeAdmin ? 'admin' : 'employee',
      };
      const res = await send({
        method: 'POST',
        url: '/admin/set-role',
        data,
        credentials: 'include',
      });
      setRequestSuccess(res);
    } catch (error) {
      setRequestError(error as Error);
      setRequestSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => setRequestSuccess(false);

  return (
    <div className="intacct-admin-inner-container">
      <GoBackButton />
      <Card>
        <CardRow>
          <CardColumn align="left">
            <Heading
              mt={2}
              title={
                makeAdmin
                  ? 'Grant SIT admin permissions'
                  : 'Revoke SIT admin permissions'
              }
            />
          </CardColumn>
        </CardRow>
        <Form
          errorCount={errors}
          onSubmit={handleSubmit}
          saveButton={
            <CardRow>
              <CardColumn>
                <Button buttonType="primary" type="submit">
                  {loading ? (
                    <Loader isActive isInsideButton />
                  ) : makeAdmin ? (
                    'Save'
                  ) : (
                    'Revoke'
                  )}
                </Button>
              </CardColumn>
            </CardRow>
          }
        >
          <Textbox
            error={
              formData.intacctCompanyId === '' && visited.intacctCompanyId
                ? 'This field is required'
                : ''
            }
            label="Intacct company ID"
            name="intacctCompanyId"
            onChange={handleInputChange}
            required
            value={formData.intacctCompanyId}
          />
          <Textbox
            error={
              formData.username === '' && visited.username
                ? 'This field is required'
                : ''
            }
            label={'Username'}
            name="username"
            onChange={handleInputChange}
            required
            value={formData.username}
          />
          <Textbox
            error={
              formData.email === '' && visited.email
                ? 'This field is required'
                : ''
            }
            label={'Email'}
            name="email"
            onChange={handleInputChange}
            required
            value={formData.email}
          />
          <Message variant="error" open={!!requestError}>
            {JSON.stringify(requestError?.message)}
          </Message>
        </Form>
      </Card>
      <Dialog onCancel={closeModal} title="Success" open={!!requestSuccess}>
        <div>
          <span>
            {makeAdmin
              ? `You've successfully granted Admin permissions to ${formData.username}`
              : `You've successfully revoked Admin permissions from ${formData.username}.`}
          </span>
        </div>
        <div className="intacct-admin-modal-footer">
          <Button buttonType="primary" onClick={closeModal}>
            OK
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

export default ChangeAdminPermissions;
